import { useState, useEffect, useCallback } from 'react';
import { kioskApi } from '../lib/kiosk-api.js';
import { StockMarkOut, ReasonPicker, reasonLabel, itemLabel } from './StockMarkOut.jsx';

// Stock Phase 1 — the 86 board on the store kiosk (docs/STOCK-SPEC.md §7.1).
// Home screen: everything out at this store with its age, reason and who marked
// it; one tap to restore with a count. Anything still out from before today
// must be answered — still out, or back in — before the board opens.

const ageColor = (days) => (days >= 7 ? '#c62828' : days >= 2 ? '#ef6c00' : '#757575');
const errText = (e) => { try { return JSON.parse(e.message).error || e.message; } catch { return e.message; } };
const btn = {
  padding: '10px 14px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff',
  fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const primary = { ...btn, background: 'var(--forest)', color: '#fff', borderColor: 'var(--forest)' };
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
// Expected-back is a day ("back tomorrow at open" is stored as that day's start), so show the day.
const backDay = (iso) => new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

// Restore control: counted items need what's on the shelf; toggle items don't.
function RestoreControl({ row, busy, onRestore }) {
  const [qty, setQty] = useState('');
  if (row.stock_class === 'toggle') {
    return <button style={primary} disabled={busy} onClick={() => onRestore(row, null)}>Back in</button>;
  }
  const n = Number(qty);
  const ok = Number.isInteger(n) && n >= 1;
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/\D/g, ''))}
        placeholder="Count" aria-label={`How many ${row.item_name} are on the shelf`}
        style={{ width: 80, padding: 10, fontSize: 16, borderRadius: 8, border: '1px solid #ccc', fontFamily: 'inherit' }} />
      <button style={ok ? primary : { ...btn, color: '#9e9e9e' }} disabled={!ok || busy} onClick={() => onRestore(row, n)}>Back in</button>
    </div>
  );
}

function OutRow({ row, busy, onRestore, onReason, prompt, onConfirm }) {
  const [giving, setGiving] = useState(false);
  if (giving) {
    return <ReasonPicker itemName={itemLabel(row)} busy={busy} onCancel={() => setGiving(false)}
      onSubmit={(r) => onReason(row, r).then(() => setGiving(false))} />;
  }
  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 14, display: 'flex',
                   gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 220, flex: 1 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>
          {itemLabel(row)}{' '}
          <span style={{ fontSize: 12, fontWeight: 700, color: ageColor(row.age_days) }}>
            {row.age_days === 0 ? 'today' : `${row.age_days}d`}
          </span>
        </div>
        <div style={{ fontSize: 13, color: '#616161', marginTop: 2 }}>
          {reasonLabel(row.reason_code)}{row.reason_note ? ` — ${row.reason_note}` : ''}
          {row.marked_by ? ` · ${row.marked_by}` : row.reason_code ? '' : ' · marked out on the register'}
          {row.out_since ? ` · since ${when(row.out_since)}` : ''}
          {row.expected_back_at ? ` · back ${backDay(row.expected_back_at)} at open` : ''}
        </div>
        {row.phantom && (
          <div style={{ fontSize: 13, color: '#c62828', fontWeight: 600, marginTop: 4 }}>
            Square says {row.on_hand} on hand, but {row.phantom.sold_last_7_days} sold in the last week — recount.
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {row.needs_reason && <button style={btn} onClick={() => setGiving(true)}>Give a reason</button>}
        {prompt && <button style={btn} disabled={busy} onClick={() => onConfirm(row)}>Still out</button>}
        <RestoreControl row={row} busy={busy} onRestore={onRestore} />
      </div>
    </div>
  );
}

export function KioskStockView({ session }) {
  const locKey = session.location.key;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [marking, setMarking] = useState(false);

  const call = useCallback((method, path, body) => kioskApi(method, `/api/kiosk/${locKey}/stock${path}`, body, session.token), [locKey, session.token]);
  const load = useCallback(async () => {
    try { setData(await call('GET', '/board')); setErr(''); } catch (e) { setErr(errText(e)); }
  }, [call]);
  useEffect(() => { if (session?.token) load(); }, [session?.token, load]);

  async function act(method, path, body) {
    setBusy(true); setErr('');
    try { await call(method, path, body); await load(); return true; }
    catch (e) { setErr(errText(e)); return false; }
    finally { setBusy(false); }
  }
  const restore = (row, quantity) => act('POST', '/in', { stock_item_id: row.stock_item_id, quantity });
  const confirm = (row) => act('POST', '/confirm', { stock_item_id: row.stock_item_id });
  const giveReason = (row, r) => act('POST', '/out', { stock_item_id: row.stock_item_id, ...r });
  const markOut = async (item, r) => { if (await act('POST', '/out', { stock_item_id: item.stock_item_id, ...r })) setMarking(false); };

  if (!data && !err) return <div style={{ color: '#757575', padding: 16 }}>Loading the 86 board…</div>;
  if (!data) return <div style={{ color: '#c62828', padding: 16 }}>{err} <button style={btn} onClick={load}>Try again</button></div>;

  const pending = data.out.filter((r) => r.needs_confirm);
  const errBar = err && <div style={{ color: '#c62828', fontWeight: 600, marginBottom: 10 }}>{err}</div>;

  if (marking) {
    return <div style={{ maxWidth: 900, margin: '0 auto' }}>{errBar}
      <StockMarkOut items={data.in_stock} busy={busy} onMark={markOut} onClose={() => setMarking(false)} /></div>;
  }

  if (pending.length > 0) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {errBar}
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 10, padding: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{pending.length} item{pending.length === 1 ? ' is' : 's are'} still marked out from before today.</div>
          <div style={{ fontSize: 14, color: '#616161', marginTop: 4 }}>For each one: still out, or count it and bring it back. A forgotten 86 hides it from online orders.</div>
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          {pending.map((row) => (
            <OutRow key={row.stock_item_id} row={row} busy={busy} prompt onConfirm={confirm} onRestore={restore} onReason={giveReason} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {errBar}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {data.out.length === 0 ? 'Nothing is 86\'d' : `${data.out.length} item${data.out.length === 1 ? '' : 's'} out`}
        </div>
        <button style={{ ...primary, padding: '12px 18px', fontSize: 15 }} onClick={() => setMarking(true)}>Mark something out</button>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {data.out.map((row) => (
          <OutRow key={row.stock_item_id} row={row} busy={busy} onRestore={restore} onReason={giveReason} />
        ))}
      </div>
      {data.modifiers_out.length > 0 && (
        <div style={{ marginTop: 18, background: '#f5f5f5', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Out on the register (milks, syrups, add-ons)</div>
          <div style={{ fontSize: 13, color: '#616161', margin: '4px 0 8px' }}>Square only lets these be changed on the register.</div>
          <div style={{ fontSize: 14 }}>{data.modifiers_out.map((m) => `${m.name}${m.list_name ? ` (${m.list_name})` : ''}`).join(' · ')}</div>
        </div>
      )}
    </div>
  );
}
